const GA4_ID = process.env['VITE_GA4_ID'] || 'G-W13NLRTL7V';
const GA4_SCRIPT_ID = 'dystest-ga4-script';
let ga4Configured = false;

export type AnalyticsEvent =
  | 'assessment_started'
  | 'camera_setup_complete'
  | 'calibration_complete'
  | 'assessment_complete'
  | 'report_emailed';

type EventParams = Record<string, string | number | boolean>;

function isConsentGiven(): boolean {
  try {
    const saved = localStorage.getItem('cookiePreferences');
    return Boolean(saved && (JSON.parse(saved) as { analytics?: boolean }).analytics);
  } catch {
    return false;
  }
}

// Call window.gtag through .call() because the global is typed as Function | undefined.
function gtagPush(...args: unknown[]): void {
  window.gtag?.call(undefined, ...args);
}

function hasGA4Script(): boolean {
  return Boolean(document.getElementById(GA4_SCRIPT_ID));
}

export function ensureGA4Loaded(): void {
  if (!isConsentGiven() || !GA4_ID) return;

  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function (...args: unknown[]): void {
      window.dataLayer?.push(args);
    };
  }

  gtagPush('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  gtagPush('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  if (!hasGA4Script()) {
    const script = document.createElement('script');
    script.id = GA4_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_ID)}`;
    script.dataset['dystestAnalytics'] = 'ga4';
    document.head.appendChild(script);
  }

  if (!ga4Configured) {
    gtagPush('js', new Date());
    gtagPush('config', GA4_ID, {
      send_page_view: false,
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      cookie_flags: 'SameSite=Lax;Secure',
    });
    ga4Configured = true;
  }
}

export function disableGA4(): void {
  if (window.gtag) {
    gtagPush('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }

  document.getElementById(GA4_SCRIPT_ID)?.remove();
  document.querySelectorAll('script[src*="googletagmanager.com/gtag/js"]').forEach((script) => {
    script.remove();
  });
  ga4Configured = false;
}

export function trackPageView(path: string, title?: string): void {
  if (!isConsentGiven() || !GA4_ID) return;
  ensureGA4Loaded();
  gtagPush('event', 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
  });
}

export function trackEvent(name: AnalyticsEvent, params?: EventParams): void {
  if (!isConsentGiven()) return;
  void import('@vercel/analytics')
    .then(({ track }) => track(name, params))
    .catch(() => { /* noop if Vercel not ready */ });
  if (GA4_ID) {
    ensureGA4Loaded();
    gtagPush('event', name, params);
  }
}
